/**
 * EntityMengen — "Mengen aus Belegen" on the object view (FR-017 #4c).
 *
 * A person's quantity links on this item, totalled per unit: Zugang, Abgang,
 * Saldo, and one row per link that opens its document. It reads the item's
 * own link rows (`useTargetLinks`, the query Belege already issues), so there
 * is no second request. The totals are `quantityTotals`': per unit, never
 * converted, exact decimals; a link it cannot read is counted, not hidden.
 *
 * Like every side panel it shows LIVE data: in the as-of view ObjectView hands
 * it the "Live-Daten" badge, since the link read has no as-of parameter.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { anchorLabel } from '../../../components/twinLinks/linkFormat';
import { displayLocale, formatDay } from '../../../components/twinLinks/linkTargetDisplay';
import { quantityTotals, type QuantityRow } from '../../../components/twinLinks/quantityTotals';
import { useTargetLinks } from '../../../hooks/useTwinLinks';
import type { EntitySourceDoc } from '../../../services/twinService';
import './EntityMengen.css';

/**
 * A canonical decimal in the active language, digit for digit — never rounded:
 * `-1240.5` → `−1.240,5` (de) / `−1,240.5` (en).
 */
function localDecimal(canonical: string): string {
  const [group, point] = displayLocale() === 'en-GB' ? [',', '.'] : ['.', ','];
  const [int, frac] = canonical.replace(/^-/, '').split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  const text = frac ? `${grouped}${point}${frac}` : grouped;
  return canonical.startsWith('-') ? `−${text}` : text;
}

function withUnit(value: string, unit: string): string {
  return unit ? `${value} ${unit}` : value;
}


interface EntityMengenProps {
  entityType: string;
  entityId: string;
  /** Live twin mode — the link read runs only when true (ObjectView's `live`). */
  live: boolean;
  docs?: EntitySourceDoc[];
  onPreview?: (docId: string) => void;
  /** ObjectView's "Live-Daten" badge while time-travelling. */
  badge?: React.ReactNode;
}

export const EntityMengen: React.FC<EntityMengenProps> = ({ entityType, entityId, live, docs, onPreview, badge }) => {
  const { t } = useTranslation();
  const source = t('twinLinks.objectSide.source');
  const { data: links } = useTargetLinks(entityType, entityId, live);
  const labels = React.useMemo(() => {
    const byId = new Map<string, string>();
    for (const doc of docs ?? []) if (doc.filename) byId.set(doc.document_id, doc.filename);
    return byId;
  }, [docs]);
  const { groups, unreadable } = React.useMemo(
    () => quantityTotals(links ?? [], (link) => labels.get(link.document_id) ?? source),
    [links, labels, source],
  );

  if (groups.length === 0 && unreadable.length === 0) return null;

  const rowLabel = (row: QuantityRow) =>
    [labels.get(row.link.document_id) ?? source, anchorLabel(row.link)].filter(Boolean).join(' · ');

  return (
    <section className="object-view__section">
      <h3 className="object-view__section-title">
        {t('twinLinks.objectSide.quantitiesTitle')}
        {badge}
      </h3>
      <div className="entity-mengen">
        {groups.map((group) => (
          <div
            key={group.unit}
            role="group"
            aria-label={t('twinLinks.objectSide.quantitiesIn', { unit: group.unit || t('twinLinks.objectSide.noUnit') })}
            className="entity-mengen__group"
          >
            <div className="entity-mengen__totals">
              <span>{`${t('twinLinks.details.in')} ${withUnit(localDecimal(group.inTotal), group.unit)}`}</span>
              <span>{`${t('twinLinks.details.out')} ${withUnit(localDecimal(group.outTotal), group.unit)}`}</span>
              <span className="entity-mengen__balance">
                {`${t('twinLinks.objectSide.balance')} ${withUnit(localDecimal(group.balance), group.unit)}`}
              </span>
            </div>
            <ul className="entity-mengen__rows">
              {group.rows.map((row) => (
                <li key={row.link.link_id}>
                  <button
                    type="button"
                    className="entity-mengen__row"
                    onClick={onPreview ? () => onPreview(row.link.document_id) : undefined}
                  >
                    <span className="entity-mengen__source">{rowLabel(row)}</span>
                    <span>{`${t(`twinLinks.details.${row.direction}`)} ${withUnit(localDecimal(row.quantity), group.unit)}`}</span>
                    {row.observedAt && <span className="entity-mengen__date">{formatDay(row.observedAt)}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {unreadable.length > 0 && (
          <p className="entity-mengen__unreadable">
            {t('twinLinks.objectSide.unreadable', { count: unreadable.length })}
          </p>
        )}
      </div>
    </section>
  );
};

export default EntityMengen;
